import { ListContextProvider, useDeleteWithConfirmController, useListContext, useRefresh } from "ra-core"
import { FC, useCallback, useRef, useState } from "react"
import { Content } from "@/components/content/Content"
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Card, Box, IconButton, Tooltip, Typography, Button, Chip, Badge, Stack } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import LockIcon from '@mui/icons-material/Lock';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import AddIcon from '@mui/icons-material/Add';
import Scrollbar from "@/components/Scrollbar"
import { Datagrid } from "@/components/list/datagrid"
import { RecPatch } from "@/dataProvider/tools"
import { FunctionField, NumberField, TextField } from "@/components/field"
import CreateOrEdit from "@/controller/CreateOrEdit"
import EditClassification from "@/components/governance/EditClassification"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import Confirm from "@/components/widgets/Confirm"
import { SecondaryClassificationResponseDto } from "@/gen/api/governance"
import useLocalListController from "@/components/list/useLocalListController"
import { useHasPermit } from "@/authProvider/useHasPermit"
import { MANAGE_DATA_CLASSIFICATIONS } from "@/constants"


type ClassificationDto = SecondaryClassificationResponseDto;

export const classificationListOptions : {sorters: Record<string, (a:ClassificationDto & RecPatch, b:ClassificationDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => 
      (a.primaryClassification || 0) - (b.primaryClassification || 0) === 0 
      ? (a.id - b.id) : ((a.primaryClassification || 0) - (b.primaryClassification || 0))
  }
}

const DataClassifications: FC<OdResourcePageProps> = (props) => {
  const { resource } = props

  const listCtrl = useLocalListController({ resource, basePath: '', filter: { includeRefCount: true } }, classificationListOptions)
  return (
    <ListContextProvider value={listCtrl}>
      <DataClassificationsBody {...props} />
    </ListContextProvider>
  )
}

const DataClassificationsBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATA_CLASSIFICATIONS])
  const listCtx = useListContext<ClassificationDto & RecPatch>()
  
  const [openEdit, setOpenEdit] = useState(false)
  const [recordToEdit, setRecordToEdit] = useState<ClassificationDto | null>(null)

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])
  const savedHandlers = useDefaultSavedHandlers(() => `${recordToEdit ? 'Edit' : 'Create'} Classification`)

  const recordToDel = useRef<ClassificationDto&RecPatch | null>(null)
  const delHandlers = useDefaultSavedHandlers('Delete Data Classification')
  delHandlers.onSuccess.merge(refreshLight)
  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: recordToDel.current as any,
    ...delHandlers,
  })

  return (
    <Content 
      pageName="Manage Data Classifications" 
      resourceName="Data Classifications" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        hasWritePermit &&
        <Button
          color="primary"
          variant="contained"
          startIcon={
            <AddIcon />
          }
          onClick={() => {
            setRecordToEdit(null)
            setOpenEdit(true)
          }}
        >
          Add Data Classification
        </Button>
      }
    >
      
      <Card>
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2, '&:nth-of-type(2)': { p: 0 } },
                '& td' : { py: 2, '&:nth-of-type(2)': { p: 0 } },
                '& .name': { maxWidth: 150, display: 'inline-block', wordBreak: 'break-word' },
                '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
              }}
              {...listCtx}
            >
              <TextField 
                source="primaryClassificationText" 
                label={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Primary</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Classification</Typography>
                  </Box>
                }
                sortable={false}
              />
              <FunctionField 
                source="name" 
                label={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Secondary</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Classification</Typography>
                  </Box>
                }
                sortable={false} 
                className="name" 
                render={(d?: ClassificationDto & RecPatch) => {
                  if (!d) { return null}
                  return (
                    <Typography variant="body1" fontWeight="lighter">{d.name}</Typography>
                  )
                }}
              />
              <TextField source="description" label="Description" sortable={false} className="description" />
              <FunctionField source="referenceCount" label="Usage Count" sortable={false} 
                render={(d?: ClassificationDto & RecPatch) => {
                  if (!d) { return null}
                  return (
                    <Stack gap={2} direction="row">
                      <Badge badgeContent={d.referenceCount ?? 0} color="info" max={999999} showZero>
                        <Chip variant="outlined" label="Fields" />
                      </Badge>
                      <Badge badgeContent={d.fileReferenceCount ?? 0} color="info" max={999999} showZero>
                        <Chip variant="outlined" label="Files" />
                      </Badge>
                    </Stack>
                  )
                }}
              />
              {hasWritePermit &&
                <FunctionField
                  label="Actions"
                  sortable={false}
                  render={(d?: ClassificationDto & RecPatch) => (
                    !d || d.isDefault ? 
                    <Tooltip title="Built In">
                      <LockIcon sx={{ width: 14 }} color="disabled" />
                    </Tooltip> :
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={() => {
                            setRecordToEdit(d || null)
                            setOpenEdit(true)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(d.referenceCount || 0) > 0 || (d.fileReferenceCount ?? 0) > 0
                        ? <DeleteForeverIcon fontSize="small" color="disabled" sx={{ p: 1, mb: '-15px'}} />
                        : <Tooltip title="Delete">
                          <IconButton
                            sx={{
                              color: 'text.primary' as any,
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                            onClick={(e:any) => {
                              recordToDel.current = d as any
                              handleDialogOpen(e)
                            }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>}
                    </>
                  )}
                  textAlign="center"
                />}
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      {hasWritePermit &&
        <>
          <CreateOrEdit resource={resource} basePath="" record={recordToEdit} onFailure={savedHandlers.onFailure} onSuccess={savedHandlers.onSuccess.merge(refreshLight)}>
            {openEdit && <EditClassification open={openEdit} setOpen={(x) => {setOpenEdit(x); if (!x) { setTimeout(() => setRecordToEdit(null)) }}} />}
          </CreateOrEdit>
          <Confirm
            isOpen={openConfirm}
            title={"Delete Data Classification"}
            loading={loading}
            onConfirm={handleDelete}
            onClose={(e:any) => {handleDialogClose(e); recordToDel.current = null}}
            confirm="Confirm"
            cancel="Cancel"
            content={(
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 1 }}
                  >
                    Please confirm whether to proceed the deletion of this
                    {` "${recordToDel.current?.primaryClassificationText}-${recordToDel.current?.name || 'Default'}" `}
                    Data Classification.
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </>}
    </Content>
  )
}

export default DataClassifications;
